import * as React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { ConfigurationModel } from 'franke-trough-dependencies';
import classNames from 'classnames';

import { GlobalPrivateState, QueryParameterState } from 'src/api';
import { ConfigurationListItem } from './ListItem/ListItem';
import { RouterState } from 'connected-react-router';

import './ConfigurationList.scss';
import { Icon } from '../../Images';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './ConfigurationList.messages';
import { requestAPIActions, RequestApiHttpPayload } from 'loopback-react-request';
import { userIsExternal } from '../../Admin/utils';
// import { filterConfigurationsBySword } from '../../User/utils';

export interface ConfigurationListProps {
  configurations: GlobalPrivateState['configurations'];
  authenticator: GlobalPrivateState['authenticator'];
  userData: GlobalPrivateState['userData'];
  location: RouterState['location'];
  params: QueryParameterState['params'];
  queryState: GlobalPrivateState['queryState'];
  user: GlobalPrivateState['user'];
  groups: GlobalPrivateState['groups'];
  folderId?: string;
  dispatch: Function;
}

export interface ConfigurationListState {
  tabListStates: any;
}

const initialTabListStates = {
  mine: {
    skip: 0,
    rowCount: 0,
    loadingMore: false,
    reachedEnd: false
  },
  others: {
    skip: 0,
    rowCount: 0,
    loadingMore: false,
    reachedEnd: false
  },
  archived: {
    skip: 0,
    rowCount: 0,
    loadingMore: false,
    reachedEnd: false
  }
}

export class ConfigurationListPartial extends React.Component<ConfigurationListProps, ConfigurationListState> {
  loadTrigger: React.RefObject<any>;
  observer: IntersectionObserver;

  constructor(props: ConfigurationListProps) {
    super(props);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.loadTrigger = React.createRef();

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.handleLoadMore();
      }
    })

    this.state = {
      tabListStates: JSON.parse(JSON.stringify(initialTabListStates))
    }
  }

  componentDidMount() {
    // this.handleLoadMore();
    this.observer.observe(this.loadTrigger.current);
  }

  componentDidUpdate(prevState: ConfigurationListProps) {
    const prevConfigurations = prevState.configurations.result;
    const prevParams = JSON.parse(JSON.stringify(prevState.queryState.params));
    const configurations = this.props.configurations.result;
    const params = JSON.parse(JSON.stringify(this.props.queryState.params));
  
    const swordChanged = params.sword !== prevParams.sword;
    const tabChanged = params.tab !== prevParams.tab;
    const configsChanged = !this.props.configurations.updating && this.props.configurations.loaded && JSON.stringify(prevConfigurations) !== JSON.stringify(configurations);
    const usersChanged = prevConfigurations !== configurations;
    const folderChanged = this.props.folderId !== prevState.folderId;
    const tab = params.tab || this.tab;

    if (swordChanged || folderChanged) {
      this.setState({
        ...this.state,
        tabListStates: JSON.parse(JSON.stringify(initialTabListStates))
      }, () => {
        this.handleLoadMore(tab); 
      });
      this.props.dispatch(requestAPIActions.loadCleared('configurations'));
    }
    
    if (tabChanged) {
      const tabListState = this.state.tabListStates[tab];
      this.setState({
        ...this.state,
        tabListStates: {
          ...this.state.tabListStates,
          [tab]: {
            ...tabListState,
            skip: 0
          }
        }
      }, () => {
        this.handleLoadMore(tab);
      });
    }

    if (configsChanged && configurations && (configurations.totalRowCount < 20)) {
      const newState = this.state;
      newState.tabListStates[tab].loadingMore = true;

      this.setState(newState);
    }
  }

  public render() {
    const { configurations, folderId, user, groups } = this.props;
    const { list, tab } = this.props.params;
    const currentGroup = folderId && groups && groups.result && Array.isArray(groups.result) && groups.result.find(g => g.id === folderId);
    const isExternal = userIsExternal(user.result);
    const canCreateFolder = !isExternal && (!currentGroup || (currentGroup && !currentGroup.parentId)) && (tab !== 'others' && tab !== 'archived');
    
    return (
      <div className="partial-configuration-list w-100 d-flex flex-wrap pb-5">
        {canCreateFolder && (
          <h5 className="heading"><FormattedMessage {...MESSAGES.configurationListHeading} /></h5>
        )}
        <div className={classNames('box-view', 'w-100')}>
          <div className="row">
            {this.configurationsForCurrentTab && this.configurationsForCurrentTab.map((configuration, index) => {
              const key = `configuration_list_item_${configuration.id}`;

              return (
                <div key={key} className={classNames('col-12 col-md-6 col-lg-4')}>
                  <div className="box d-flex">
                    <ConfigurationListItem configuration={configuration} canMoveFolder={tab !== 'others' && tab !== 'archived'} />
                  </div>
                </div>
              );
            }) || (
              <div className="folder-none col-12">
                <FormattedMessage {...MESSAGES.configurationListNone} />
              </div>
            )}
          </div>
          {(configurations.updating || !configurations.loaded) && (
            <div className="inline-loading">
              <div className="lds-ring"><div /><div /><div /><div /></div>
            </div>
          )}
          <div ref={this.loadTrigger} />
        </div>
      </div>
    );
  }

  private get configurations(): ConfigurationModel[] | undefined {
    const { configurations } = this.props;
    if (!configurations.result || configurations.result && configurations.result.rows && configurations.result.rows.length === 0) {
      return;
    }

    return configurations && configurations.result.rows;
  }

  private get configurationsForCurrentTab(): ConfigurationModel[] | undefined {
    const { configurations, authenticator, userData, params, folderId } = this.props;
    if (!configurations.result || !configurations.result.rows || configurations.result.rows.length === 0 || !authenticator.accessToken || !userData.result) {
      return;
    }

    const tab = params.tab || 'mine';
    const userId = authenticator.accessToken.userId.toString();
    const archivedConfigurations = userData.result.archivedConfigurations || [];
    const deletedConfigurations = userData.result.deletedConfigurations || [];
    let configurationsForTab;

    // We keep filtering in front-end... since we save all configurations on tab change
    if (tab === 'mine') {
      configurationsForTab = configurations.result.rows.filter(c => {
        if (folderId && c.folderId !== folderId) {
          return false;
        }
        if (!folderId && c.folderId) {
          return false;
        }
        
        return c.userId === userId && !archivedConfigurations.includes(c.id)
      });
    } else if (tab === 'others') {
      configurationsForTab = configurations.result.rows.filter(c => c.userId !== userId && !archivedConfigurations.includes(c.id));
    } else if (tab === 'archived') {
      configurationsForTab = configurations.result.rows.filter(c => archivedConfigurations.includes(c.id));
    }

    // Deleted tabs
    if (configurationsForTab) {
      configurationsForTab = configurationsForTab.filter(c => !deletedConfigurations.includes(c.id) && !c.deleted);

      // Duplicates...
      const existingIds: string[] = [];
      configurationsForTab = configurationsForTab.filter(c => {
        if (!existingIds.includes(c.id)) {
          existingIds.push(c.id);
          return c;
        }

        return null;
      });
    }

    return configurationsForTab;
  }

  get sword() {
    const sword = this.props.queryState.params.sword;
    return sword;
  }

  private handleLoadMore(tab?: any) {
    const { configurations, params, folderId } = this.props;
    if (!tab) {tab = this.tab;}

    const tabListState = this.state.tabListStates[tab];

    // console.log('loading more', !tabListState.loadingMore, !configurations.updating)
    if (!tabListState.loadingMore && !configurations.updating) {
      this.setState({
        ...this.state,
        tabListStates: {
          ...this.state.tabListStates,
          [tab]: {
            ...tabListState,
            loadingMore: true
          }
        }
      }, () => {
        // setTimeout(() => { // Sometimes the tab is not set before we load?

        const queryParams: any = {
          ...params,
          limit: 20,
          skip: tabListState.skip
        }

        if (folderId) {
          queryParams.folderId = folderId;
        } else {
          queryParams.folderId = null;
        }

        this.props.dispatch(requestAPIActions.load<RequestApiHttpPayload>(
          'configurations', {
            url: `/Configurations/dashboard-list`,
            queryParams,
            isPager: true
          },
          true
        ));

        this.setState({
          ...this.state,
          tabListStates: {
            ...this.state.tabListStates,
            [tab]: {
              skip: queryParams.skip + queryParams.limit,
              loadingMore: false
            }
          }
        });
        // }, 10);
      });
    }
  }
  
  get tab() {
    return this.props.queryState.params.tab || 'mine';
  }
}

export const ConfigurationList = connect((state: GlobalPrivateState) => ({
  configurations: state.configurations,
  groups: state.groups,
  userData: state.userData,
  authenticator: state.authenticator,
  location: state.router.location,
  params: state.queryState.params,
  queryState: state.queryState,
  user: state.user
}))(ConfigurationListPartial);

export default ConfigurationList;